import type { ReactNode } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ApiError } from '@/api/errors';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusChip } from '@/components/common/StatusChip';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { useOrganizationProfile, useTeamInvitations, useTeamMembers } from '@/features/team/hooks';
import { memberStatus, roleDescription, roleLabel } from '@/features/team/roles';
import type { TeamInvitation, TeamMember } from '@/features/team/types';
import { ArrowPathIcon, BuildingOffice2Icon, EnvelopeIcon, ExclamationTriangleIcon, InformationCircleIcon, LockClosedIcon, UsersIcon } from '@/icons';

const dateFmt = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' });
const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d);
};

/** a***@example.com — invitee addresses are shown in full only to people who may invite. */
const maskEmail = (email: string) => {
  const [local, domain] = email.split('@');
  if (!domain) return '***';
  return `${local.slice(0, 1)}***@${domain}`;
};

function Section({ id, title, icon: Icon, count, children }: {
  id: string; title: string; icon: typeof UsersIcon; count?: number; children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="rounded-xl border border-border bg-surface">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Icon className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
        <h2 id={id} className="text-sm font-semibold text-fg">{title}</h2>
        {count !== undefined && <span className="text-xs text-fg-muted">({count})</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function NoAccess({ what }: { what: string }) {
  return (
    <div role="status" className="flex items-center gap-2 py-6 text-sm text-fg-muted">
      <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
      <span>Your role doesn&apos;t include viewing {what}. Ask an owner or administrator if you need access.</span>
    </div>
  );
}

/**
 * A failed load, worded by cause, so a sign-in, permission or server problem is never mistaken for
 * "no data". Server and network failures offer Try again; nothing on the page is ever changed.
 */
function SectionError({ error, what, onRetry }: { error: unknown; what: string; onRetry: () => void }) {
  const kind = error instanceof ApiError ? error.kind : 'unknown';
  if (kind === 'forbidden') {
    return (
      <div role="alert" className="flex items-center gap-2 py-6 text-sm text-fg-muted">
        <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
        <span>Access to {what} was refused for your role. Ask an owner or administrator if you need access.</span>
      </div>
    );
  }
  if (kind === 'unauthenticated') {
    return (
      <div role="alert" className="flex items-center gap-2 py-6 text-sm text-fg">
        <LockClosedIcon className="h-4 w-4 text-warning" aria-hidden="true" />
        <span>Your session has expired. <Link className="font-medium text-brand underline" to="/login">Sign in again</Link> to see {what}.</span>
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-col items-start gap-3 py-6 text-sm text-fg">
      <span className="flex items-center gap-2">
        <ExclamationTriangleIcon className="h-4 w-4 text-danger" aria-hidden="true" />
        <span>
          {kind === 'network'
            ? `We couldn't reach the server to load ${what}. Check your connection and try again.`
            : `We couldn't load ${what} right now. This is a problem on our side; nothing has been changed.`}
        </span>
      </span>
      <Button size="sm" variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} onPress={onRetry}>Try again</Button>
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="space-y-2 py-2">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded-md bg-surface-raised" aria-hidden="true" />)}
    </div>
  );
}

function OrganizationCard() {
  const org = useOrganizationProfile();
  if (!org.allowed) return <NoAccess what="the organization profile" />;
  if (org.isLoading) return <Loading label="Loading organization profile…" />;
  if (org.isError && org.error instanceof ApiError && org.error.kind === 'not_found') {
    return <EmptyState icon={BuildingOffice2Icon} title="No organization profile yet" description="This account doesn't have organization details set up. Contact support if this looks wrong." />;
  }
  if (org.isError || !org.data) return <SectionError error={org.error} what="the organization profile" onRetry={() => org.refetch()} />;
  const o = org.data;
  const rows: Array<[string, ReactNode]> = [
    ['Organization name', o.displayName ?? o.legalName],
    ['Registered name', o.legalName],
    ['Account code', <span key="code" className="font-mono">{o.accountCode}</span>],
    ['Country', o.countryCode ?? '—'],
    ['VAT number', o.vatNumber ?? '—'],
    ['Support email', o.supportEmail ?? '—'],
    ['Two-step sign-in required', o.enforceMfa ? 'Yes' : 'No'],
    ['Customer since', formatDate(o.createdAt)],
  ];
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="text-xs text-fg-muted">{k}</dt>
          <dd className="text-sm text-fg">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function MembersTable({ members, currentUserId }: { members: TeamMember[]; currentUserId?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Members of this organization</caption>
        <thead className="text-xs text-fg-muted">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">Member</th>
            <th scope="col" className="py-2 pr-4 font-medium">Role</th>
            <th scope="col" className="py-2 pr-4 font-medium">Status</th>
            <th scope="col" className="py-2 font-medium">Joined</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const status = memberStatus(m.status);
            const isYou = m.userId === currentUserId;
            return (
              <tr key={m.id} className="border-t border-border">
                <td className="py-2 pr-4">
                  <span className="font-medium text-fg">{isYou ? 'You' : 'Team member'}</span>
                  {!isYou && <span className="ml-2 font-mono text-xs text-fg-muted">#{m.userId.slice(0, 8)}</span>}
                </td>
                <td className="py-2 pr-4">
                  <span title={roleDescription(m.roleKey)}>{roleLabel(m.roleKey)}</span>
                  {m.isOwner && m.roleKey !== 'owner' && <span className="ml-2 text-xs text-fg-muted">(owner)</span>}
                </td>
                <td className="py-2 pr-4"><StatusChip tone={status.tone}>{status.label}</StatusChip></td>
                <td className="py-2">{formatDate(m.joinedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MembersSection() {
  const { user } = useAuth();
  const members = useTeamMembers();
  const count = members.allowed && members.data ? members.data.length : undefined;
  return (
    <Section id="team-members" title="Members" icon={UsersIcon} count={count}>
      {!members.allowed ? <NoAccess what="members" />
        : members.isLoading ? <Loading label="Loading members…" />
          : members.isError ? <SectionError error={members.error} what="members" onRetry={() => members.refetch()} />
            : !members.data?.length
              ? <EmptyState icon={UsersIcon} title="No members yet" description="People appear here once they join this organization." />
              : <MembersTable members={members.data} currentUserId={user?.id} />}
    </Section>
  );
}

function InvitationsTable({ invitations, showEmail }: { invitations: TeamInvitation[]; showEmail: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Pending invitations</caption>
        <thead className="text-xs text-fg-muted">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">Invited email</th>
            <th scope="col" className="py-2 pr-4 font-medium">Role</th>
            <th scope="col" className="py-2 pr-4 font-medium">Sent</th>
            <th scope="col" className="py-2 font-medium">Expires</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((i) => (
            <tr key={i.id} className="border-t border-border">
              <td className="py-2 pr-4">{showEmail ? i.email : maskEmail(i.email)}</td>
              <td className="py-2 pr-4">{roleLabel(i.roleKey)}</td>
              <td className="py-2 pr-4">{formatDate(i.createdAt)}</td>
              <td className="py-2">{formatDate(i.expiresAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvitationsSection() {
  const { can } = usePermission();
  const invitations = useTeamInvitations();
  const count = invitations.allowed && invitations.data ? invitations.data.length : undefined;
  return (
    <Section id="team-invitations" title="Pending invitations" icon={EnvelopeIcon} count={count}>
      {!invitations.allowed ? <NoAccess what="invitations" />
        : invitations.isLoading ? <Loading label="Loading invitations…" />
          : invitations.isError ? <SectionError error={invitations.error} what="invitations" onRetry={() => invitations.refetch()} />
            : !invitations.data?.length
              ? <EmptyState icon={EnvelopeIcon} title="No pending invitations" description="Invitations that haven't been accepted yet appear here." />
              : <InvitationsTable invitations={invitations.data} showEmail={can('member:invite')} />}
    </Section>
  );
}

/**
 * Team & organization (read-only). Shows the active organization's profile, members (role, status,
 * joined) and pending invitations from organization-service. It never changes anything; inviting
 * people and changing roles come later, once two-step sign-in is available.
 */
export function TeamPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="Team & organization" description="Who has access to this organization, and its account details." />
      <p className="flex items-start gap-2 rounded-lg bg-info/10 px-3 py-2 text-sm text-fg">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
        <span>This page is view-only for now. Inviting people and changing roles will be added in a later update.</span>
      </p>
      <Section id="team-organization" title="Organization" icon={BuildingOffice2Icon}>
        <OrganizationCard />
      </Section>
      <MembersSection />
      <InvitationsSection />
    </div>
  );
}
